import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { firestore, firebaseAuth } from '../firebase';
import './WishlistButton.css';
import { useToast } from '../Context/ToastContext';

const WishlistButton = ({ productId }) => {
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const { success, error: showError } = useToast();

  const userSignin = useSelector(state => state.userSignin);
  const { userInfo } = userSignin;
  const [authUser, setAuthUser] = useState(null);
  const currentUser = firebaseAuth.currentUser;
  const uid = (authUser && authUser.uid) || (userInfo && userInfo.uid) || (currentUser && currentUser.uid) || null;

  // Keep Firebase auth state in sync to avoid stale currentUser reads
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, (u) => setAuthUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (uid && productId) {
      checkWishlistStatus(uid);
    } else {
      setIsInWishlist(false);
    }
  }, [uid, productId]);

  const checkWishlistStatus = async (resolvedUid) => {
    try {
      const wishlistDoc = await getDoc(
        doc(firestore, 'USERS', resolvedUid, 'USER_DATA', 'MY_WISHLIST')
      );
      
      if (wishlistDoc.exists()) {
        const wishlistData = wishlistDoc.data();
        const listSize = wishlistData.list_size || 0;
        
        for (let i = 0; i < listSize; i++) {
          if (wishlistData[`product_ID_${i}`] === productId) {
            setIsInWishlist(true);
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error checking wishlist status:', error);
    }
  };

  const handleWishlistToggle = async () => {
    if (!uid) {
      // Redirect to sign in
      window.location.href = '/signin';
      return;
    }

    if (loading) return;

    setLoading(true);
    try {
      const wishlistRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_WISHLIST');
      const wishlistDoc = await getDoc(wishlistRef);
      
      if (isInWishlist) {
        // Remove from wishlist
        await removeFromWishlist(wishlistRef, wishlistDoc);
      } else {
        // Add to wishlist
        await addToWishlist(wishlistRef, wishlistDoc);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      showError('Failed to update wishlist');
    }
    setLoading(false);
  };

  const addToWishlist = async (wishlistRef, wishlistDoc) => {
    const data = wishlistDoc.exists() ? (wishlistDoc.data() || {}) : {};
    const size = Number(data.list_size) || 0;
    // Build current list from existing keys
    const arr = [];
    for (let i = 0; i < size; i++) {
      const pid = data[`product_ID_${i}`];
      if (pid) arr.push(pid);
    }
    // Avoid duplicates
    if (!arr.includes(productId)) arr.push(productId);
    const payload = { list_size: arr.length };
    arr.forEach((pid, idx) => payload[`product_ID_${idx}`] = pid);
    // Overwrite to avoid stale fields
    await setDoc(wishlistRef, payload, { merge: false });
    setIsInWishlist(true);
    success('Product added to wishlist!');

    // Trigger header count refresh
    if (window.refreshHeaderCounts) {
        window.refreshHeaderCounts();
    }
  };

  const removeFromWishlist = async (wishlistRef, wishlistDoc) => {
    if (!wishlistDoc.exists()) return;
    const data = wishlistDoc.data() || {};
    const size = Number(data.list_size) || 0;
    const arr = [];
    for (let i = 0; i < size; i++) {
      const pid = data[`product_ID_${i}`];
      if (pid && pid !== productId) arr.push(pid);
    }
    const payload = { list_size: arr.length };
    arr.forEach((pid, idx) => payload[`product_ID_${idx}`] = pid);
    await setDoc(wishlistRef, payload, { merge: false });
    setIsInWishlist(false);
    success('Product removed from wishlist!');

    // Trigger header count refresh
    if (window.refreshHeaderCounts) {
        window.refreshHeaderCounts();
    }
  };

  return (
    <button 
      className={`wishlist-btn ${isInWishlist ? 'active' : ''} ${loading ? 'loading' : ''}`}
      onClick={handleWishlistToggle}
      disabled={loading}
    >
      <i className={`${isInWishlist ? 'fas' : 'far'} fa-heart`}></i>
      {loading ? 'Loading...' : isInWishlist ? 'In Wishlist' : 'Add to Wishlist'}
    </button>
  );
};

export default WishlistButton;
